import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DICT, LANGUAGES } from "../../lib/i18n";
import {
  ArrowLeft,
  CheckCircle,
  Warning,
  XCircle,
  ArrowsClockwise,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";

/**
 * /admin/i18n-coverage  (hidden route, admin/manager only)
 *
 * QA dashboard for the DICT in /lib/i18n.jsx. Catches:
 *  • Missing keys (translation drift) in any non-EN language
 *  • Extra keys present in a non-EN dict but not in EN (also drift)
 *  • Mojibake regression — double-encoded UTF-8 patterns like Ã³ / Ø£ /
 *    Â· that should never appear in a clean dict
 *  • Empty string values (untranslated placeholder)
 *  • Identical-to-EN values in non-EN dicts (likely untranslated)
 *
 * Why this page exists:
 *   In Feb 2026 we fixed 3,126 mojibake strings across 7 languages. To prevent
 *   that class of bug from silently re-shipping, this page is the canary —
 *   a quick visual sanity check anyone can run before merging i18n changes.
 *
 * Pure client-side (DICT is already in the bundle). No backend call.
 */

// Mojibake = UTF-8 bytes mis-read as Latin-1, then re-encoded as UTF-8.
// Common markers: capital Latin letter + a high-bit Latin-1 char.
const MOJIBAKE_RE = /[\u00C2\u00C3\u00D8\u00D9][\u0080-\u017F]/;

function statsForLang(code, dict) {
  const enKeys = Object.keys(DICT.en);
  const myKeys = Object.keys(dict);
  const enSet = new Set(enKeys);
  const mySet = new Set(myKeys);
  const missing = enKeys.filter((k) => !mySet.has(k));
  const extras = myKeys.filter((k) => !enSet.has(k));
  let mojibake = 0;
  let empty = 0;
  let identical_to_en = 0;
  const mojibakeKeys = [];
  const identicalKeys = [];
  const emptyKeys = [];
  for (const k of myKeys) {
    const v = dict[k];
    if (!v || (typeof v === "string" && v.trim() === "")) {
      empty++;
      emptyKeys.push(k);
      continue;
    }
    if (typeof v === "string" && MOJIBAKE_RE.test(v)) {
      mojibake++;
      mojibakeKeys.push(k);
    }
    // English is the source so don't flag identical against itself.
    if (code !== "en" && DICT.en[k] === v && typeof v === "string" && v.trim() !== "") {
      identical_to_en++;
      identicalKeys.push(k);
    }
  }
  return {
    code,
    total: myKeys.length,
    en_total: enKeys.length,
    missing: missing.length,
    extras: extras.length,
    mojibake,
    empty,
    identical_to_en,
    coverage_pct: enKeys.length === 0 ? 100 : Math.round(((enKeys.length - missing.length) / enKeys.length) * 100),
    mojibake_sample: mojibakeKeys.slice(0, 8),
    missing_sample: missing.slice(0, 8),
    extras_sample: extras.slice(0, 8),
    identical_sample: identicalKeys.slice(0, 8),
    empty_sample: emptyKeys.slice(0, 4),
  };
}

function pickRandomKeys(n) {
  const enKeys = Object.keys(DICT.en);
  const shuffled = [...enKeys].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

function Pill({ value, tone = "ok", testid }) {
  const tones = {
    ok: "bg-emerald-100 text-emerald-700 border-emerald-300",
    warn: "bg-amber-100 text-amber-800 border-amber-300",
    bad: "bg-rose-100 text-rose-800 border-rose-300",
    neutral: "bg-stone-100 text-[#4A5C50] border-stone-300",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide border ${tones[tone]}`}
      data-testid={testid}
    >
      {value}
    </span>
  );
}

export default function I18nCoverage() {
  const [showOnlyProblems, setShowOnlyProblems] = useState(false);
  const [sampleKeys, setSampleKeys] = useState(() => pickRandomKeys(10));
  const [expandedLang, setExpandedLang] = useState(null);

  const allStats = useMemo(() => {
    const codes = Object.keys(DICT);
    return codes.map((c) => statsForLang(c, DICT[c]));
  }, []);

  const visibleStats = useMemo(() => {
    if (!showOnlyProblems) return allStats;
    return allStats.filter(
      (s) =>
        s.code !== "en" &&
        (s.missing > 0 || s.extras > 0 || s.mojibake > 0 || s.empty > 0 || s.identical_to_en > 5),
    );
  }, [allStats, showOnlyProblems]);

  const overall = useMemo(() => {
    const non_en = allStats.filter((s) => s.code !== "en");
    return {
      languages: allStats.length,
      en_keys: allStats[0]?.en_total || 0,
      total_missing: non_en.reduce((a, s) => a + s.missing, 0),
      total_extras: non_en.reduce((a, s) => a + s.extras, 0),
      total_mojibake: non_en.reduce((a, s) => a + s.mojibake, 0),
      total_empty: non_en.reduce((a, s) => a + s.empty, 0),
      clean: non_en.every((s) => s.missing === 0 && s.extras === 0 && s.mojibake === 0 && s.empty === 0),
    };
  }, [allStats]);

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1 text-[#4A5C50] hover:text-[#112318] text-sm mb-6" data-testid="back-to-admin">
          <ArrowLeft size={16} /> Back to Admin
        </Link>

        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
          <h1 className="serif text-4xl text-[#112318]" data-testid="i18n-coverage-heading">
            i18n coverage QA
          </h1>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide border ${
              overall.clean
                ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                : "bg-amber-100 text-amber-800 border-amber-300"
            }`}
            data-testid="overall-status-pill"
          >
            {overall.clean ? (
              <>
                <CheckCircle size={14} weight="fill" /> ALL CLEAN
              </>
            ) : (
              <>
                <Warning size={14} weight="fill" /> {overall.total_missing + overall.total_mojibake + overall.total_empty + overall.total_extras} ISSUES
              </>
            )}
          </span>
        </div>
        <p className="text-[#4A5C50] mt-2 leading-relaxed max-w-3xl">
          Compares every language block in <code className="text-[#184A30]">/lib/i18n.jsx</code> against the English source.
          Catches missing keys, extras, double-encoded mojibake (e.g. <code>Ã³</code>/<code>Ø£</code>), empty values, and untranslated lines.
          Pure client-side — no backend call. Run this before merging any translation PR.
        </p>

        {/* Top summary tiles */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-tiles">
          <SummaryTile label="Languages" value={overall.languages} tone="neutral" />
          <SummaryTile label="EN source keys" value={overall.en_keys} tone="neutral" />
          <SummaryTile label="Missing keys" value={overall.total_missing} tone={overall.total_missing > 0 ? "bad" : "ok"} testid="tile-missing" />
          <SummaryTile label="Mojibake hits" value={overall.total_mojibake} tone={overall.total_mojibake > 0 ? "bad" : "ok"} testid="tile-mojibake" />
          <SummaryTile label="Empty values" value={overall.total_empty} tone={overall.total_empty > 0 ? "warn" : "ok"} testid="tile-empty" />
        </div>

        {/* Controls */}
        <div className="mt-6 flex items-center gap-3 text-sm">
          <button
            onClick={() => setShowOnlyProblems((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E0D8] bg-white px-3 py-1 text-[#4A5C50] hover:bg-[#FBF9F6]"
            data-testid="toggle-only-problems"
          >
            {showOnlyProblems ? <Eye size={14} /> : <EyeSlash size={14} />}
            {showOnlyProblems ? "Showing only languages with problems" : "Show all languages"}
          </button>
          <button
            onClick={() => setSampleKeys(pickRandomKeys(10))}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E0D8] bg-white px-3 py-1 text-[#4A5C50] hover:bg-[#FBF9F6]"
            data-testid="reshuffle-samples"
          >
            <ArrowsClockwise size={14} /> Reshuffle samples
          </button>
        </div>

        {/* Per-language table */}
        <section className="mt-6 space-y-3" data-testid="per-language-list">
          {visibleStats.map((s) => {
            const isExpanded = expandedLang === s.code;
            const isEn = s.code === "en";
            const hasIssues = s.missing > 0 || s.extras > 0 || s.mojibake > 0 || s.empty > 0;
            const langMeta = LANGUAGES.find((l) => l.code === s.code);
            return (
              <div
                key={s.code}
                className="rounded-2xl border border-[#E5E0D8] bg-white overflow-hidden"
                data-testid={`lang-row-${s.code}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedLang(isExpanded ? null : s.code)}
                  className="w-full flex items-baseline justify-between gap-4 px-5 py-4 hover:bg-[#FBF9F6] text-left"
                  data-testid={`lang-toggle-${s.code}`}
                >
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="serif text-2xl text-[#112318] uppercase tracking-wide">{s.code}</span>
                    {langMeta && <span className="text-sm text-[#4A5C50]">{langMeta.label}</span>}
                    {isEn && <Pill value="SOURCE" tone="neutral" testid={`pill-source-${s.code}`} />}
                    {!isEn && (
                      <>
                        <Pill
                          value={`${s.coverage_pct}% coverage`}
                          tone={s.coverage_pct === 100 ? "ok" : s.coverage_pct >= 95 ? "warn" : "bad"}
                          testid={`pill-coverage-${s.code}`}
                        />
                        {s.missing > 0 && (
                          <Pill value={`${s.missing} missing`} tone="bad" testid={`pill-missing-${s.code}`} />
                        )}
                        {s.extras > 0 && (
                          <Pill value={`${s.extras} extra`} tone="warn" testid={`pill-extras-${s.code}`} />
                        )}
                        {s.mojibake > 0 && (
                          <Pill value={`${s.mojibake} mojibake`} tone="bad" testid={`pill-mojibake-${s.code}`} />
                        )}
                        {s.empty > 0 && (
                          <Pill value={`${s.empty} empty`} tone="warn" testid={`pill-empty-${s.code}`} />
                        )}
                        {s.identical_to_en > 5 && (
                          <Pill
                            value={`${s.identical_to_en} same as EN`}
                            tone="warn"
                            testid={`pill-identical-${s.code}`}
                          />
                        )}
                        {!hasIssues && s.identical_to_en <= 5 && (
                          <Pill value="CLEAN" tone="ok" testid={`pill-clean-${s.code}`} />
                        )}
                      </>
                    )}
                  </div>
                  <span className="text-xs text-[#4A5C50]">
                    {s.total}/{s.en_total} keys {isExpanded ? "▴" : "▾"}
                  </span>
                </button>

                {isExpanded && (
                  <div
                    className="border-t border-[#E5E0D8] bg-[#FBF9F6] px-5 py-4 space-y-4"
                    data-testid={`lang-detail-${s.code}`}
                  >
                    {s.mojibake > 0 && (
                      <ProblemSection
                        title={`Mojibake (${s.mojibake})`}
                        tone="bad"
                        items={s.mojibake_sample}
                        renderValue={(k) => (
                          <span className="text-rose-700 mono">{DICT[s.code][k]}</span>
                        )}
                      />
                    )}
                    {s.missing > 0 && (
                      <ProblemSection
                        title={`Missing (${s.missing})`}
                        tone="bad"
                        items={s.missing_sample}
                        renderValue={() => <span className="text-stone-500 italic">— (not translated)</span>}
                      />
                    )}
                    {s.extras > 0 && (
                      <ProblemSection
                        title={`Extras (${s.extras}) — keys in this dict but not in EN`}
                        tone="warn"
                        items={s.extras_sample}
                        renderValue={(k) => (
                          <span className="text-amber-800 mono">{DICT[s.code][k]}</span>
                        )}
                      />
                    )}
                    {s.empty > 0 && (
                      <ProblemSection
                        title={`Empty (${s.empty})`}
                        tone="warn"
                        items={s.empty_sample}
                        renderValue={() => <span className="text-stone-500 italic">""</span>}
                      />
                    )}
                    {!isEn && s.identical_to_en > 5 && (
                      <ProblemSection
                        title={`Likely untranslated (${s.identical_to_en} values identical to EN)`}
                        tone="warn"
                        items={s.identical_sample}
                        renderValue={(k) => (
                          <span className="text-amber-800">{DICT[s.code][k]}</span>
                        )}
                      />
                    )}
                    {!hasIssues && s.identical_to_en <= 5 && (
                      <p className="text-sm text-emerald-700 flex items-center gap-1">
                        <CheckCircle size={14} weight="fill" /> No drift detected.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* Side-by-side sample */}
        <section className="mt-12" data-testid="sample-comparison">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <h2 className="serif text-2xl text-[#112318]">Random sample — eyeball check</h2>
            <span className="text-xs tracking-[0.15em] uppercase text-[#4A5C50]">
              10 random keys side-by-side
            </span>
          </div>
          <p className="text-sm text-[#4A5C50] mt-1 leading-relaxed">
            Spot-check that translations look right. Click <strong>Reshuffle samples</strong> above to get a new set.
          </p>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E5E0D8] bg-white">
            <table className="w-full text-sm" data-testid="sample-table">
              <thead className="bg-[#FBF9F6]">
                <tr>
                  <th className="text-left px-3 py-2 text-[#4A5C50] tracking-wide text-[11px] uppercase">Key</th>
                  {Object.keys(DICT).map((code) => (
                    <th key={code} className="text-left px-3 py-2 text-[#4A5C50] tracking-wide text-[11px] uppercase">
                      {code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sampleKeys.map((k) => (
                  <tr key={k} className="border-t border-[#E5E0D8]" data-testid={`sample-row-${k}`}>
                    <td className="px-3 py-2 mono text-[10px] text-[#184A30] whitespace-nowrap align-top">{k}</td>
                    {Object.keys(DICT).map((code) => {
                      const v = DICT[code][k];
                      const mojibake = v && typeof v === "string" && MOJIBAKE_RE.test(v);
                      return (
                        <td
                          key={code}
                          className={`px-3 py-2 align-top text-[12px] ${
                            !v ? "text-stone-400 italic" : mojibake ? "text-rose-700" : "text-[#112318]"
                          }`}
                        >
                          {v || "—"}
                          {mojibake && (
                            <XCircle size={11} weight="fill" className="inline-block ml-1 text-rose-600 mb-0.5" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone = "neutral", testid }) {
  const colors = {
    ok: "border-emerald-300 bg-emerald-50",
    warn: "border-amber-300 bg-amber-50",
    bad: "border-rose-300 bg-rose-50",
    neutral: "border-[#E5E0D8] bg-white",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[tone]}`} data-testid={testid}>
      <p className="text-[10px] tracking-[0.15em] uppercase text-[#4A5C50] font-semibold">{label}</p>
      <p className="serif text-3xl text-[#112318] mt-1">{value}</p>
    </div>
  );
}

function ProblemSection({ title, tone, items, renderValue }) {
  const colors = {
    bad: "text-rose-800",
    warn: "text-amber-800",
    ok: "text-emerald-800",
  };
  return (
    <div>
      <p className={`text-[11px] tracking-[0.15em] uppercase font-semibold ${colors[tone]}`}>{title}</p>
      <ul className="mt-2 space-y-1 text-xs">
        {items.map((k) => (
          <li key={k} className="grid grid-cols-[260px_1fr] gap-3 items-baseline">
            <code className="text-[10px] text-[#184A30] mono truncate" title={k}>{k}</code>
            <div>{renderValue(k)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
